import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { CardBody } from "../../../_metronic/_partials/controls";
import { postProduct, updateProduct } from "../../../api/product";
import { postProductMarca } from "../../../api/productMarca";
import { alertError, alertSuccess } from "../../../utils/logger";
import DurationTextField from "../fields/DurationTextField";

function getEmptyProduct() {
	return {
		referencia: null,
		descripcion: null,
		familia_id: null,
		marca_id: null,
		tipo_producto_id: null,
		precio: null,
		tipo_unidad_id: null,
		tiempo_instalacion: null,
		descuento_base: null,
	};
}

const CreateProductDialog = (props) => {
	const {
		title,
		open,
		setOpen,
		productFamilies,
		productMarcas,
		productTypes,
		productUnitTypes,
		onSave,
		productToEdit = null,
	} = props;

	const [product, setProduct] = useState(getEmptyProduct());
	const [marcas, setMarcas] = useState(productMarcas || []);
	const [openCreateMarcaDialog, setOpenCreateMarcaDialog] = useState(false);
	const [newMarcaName, setNewMarcaName] = useState("");
	const [installationTimeIsValid, setInstallationTimeIsValid] = useState(true);
	const isLabor = productTypes?.some(
		(type) => type.id === product?.tipo_producto_id && type.es_mano_de_obra
	);

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	React.useEffect(() => {
		setMarcas(productMarcas || []);
	}, [productMarcas]);

	React.useEffect(() => {
		if (open) setInstallationTimeIsValid(true);
	}, [open]);

	React.useEffect(() => {
		if (!open) return;
		setProduct(
			productToEdit
				? {
					...getEmptyProduct(),
					...productToEdit,
					familia_id: productToEdit.familia_id || productToEdit.familia?.id || null,
					marca_id: productToEdit.marca_id || productToEdit.marca?.id || null,
					tipo_producto_id: productToEdit.tipo_producto_id || productToEdit.tipo_producto?.id || null,
					tipo_unidad_id: productToEdit.tipo_unidad_id || productToEdit.tipo_unidad?.id || null,
				}
				: getEmptyProduct()
		);
	}, [open, productToEdit]);

	const saveMarca = async () => {
		if (!newMarcaName.trim()) {
			alertError({ error: "El nombre de la marca es obligatorio." });
			return;
		}

		try {
			const response = await postProductMarca({ nombre: newMarcaName.trim() }, loggedUser?.accessToken);
			if (response.status === 201 || response.status === 200) {
				setMarcas((current) => [...current, response.data]);
				setProduct((current) => ({ ...current, marca_id: response.data.id }));
				setNewMarcaName("");
				setOpenCreateMarcaDialog(false);
				alertSuccess({ title: "¡Guardada!", customMessage: "Marca creada con éxito." });
			}
		} catch (error) {
			alertError({ error, customMessage: "No se ha podido crear la marca." });
		}
	};
	function saveProduct() {
		const validateProduct = () => {
			let error = "";

			if (!product?.descripcion) {
				error = "La descripción es obligatoria.";
			} else if (!product?.referencia) {
				error = "La referencia es obligatoria.";
			} else if (!product?.familia_id) {
				error = "La familia es obligatoria.";
			} else if (!product?.tipo_producto_id) {
				error = "El tipo de producto es obligatorio.";
			} else if (!product?.tipo_unidad_id) {
				error = "El tipo de unidad es obligatorio.";
			} else if (!isLabor && (product?.precio === null || product?.precio === "")) {
				error = "El precio es obligatorio.";
			} else if (!installationTimeIsValid || !product?.tiempo_instalacion) {
				error = "El tiempo de instalación es obligatorio.";
			}

			if (error) {
				alertError({
					error: error,
				});
				return false;
			}

			return true;
		};

		if (!validateProduct()) {
			return;
		}

		const payload = isLabor
			? { ...product, precio: 0, descuento_base: 0 }
			: product;
		const request = productToEdit?.id
			? updateProduct(productToEdit.id, payload, loggedUser?.accessToken)
			: postProduct(payload, loggedUser?.accessToken);

		request
			.then((res) => {
				if ([200, 201, 204].includes(res.status)) {
					alertSuccess({
						title: "¡Guardado!",
						customMessage: productToEdit
							? "Cambios guardados con éxito."
							: "Producto guardado con éxito.",
					});
					onSave(res.data?.id || productToEdit?.id);
					setProduct(getEmptyProduct());
					setOpen(false);
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: productToEdit
						? "No se han podido guardar los cambios."
						: "No se ha podido guardar el producto.",
				});
			});
	}

	const handleChange = (element) => (event) => {
		setProduct({ ...product, [element]: event.target.value });
	};

	const handleInstallationTimeChange = (seconds) => {
		setInstallationTimeIsValid(seconds !== null);
		if (seconds !== null) {
			setProduct((current) => ({ ...current, tiempo_instalacion: seconds }));
		}
	};

	return (
		<>
			<Dialog
				fullWidth={true}
				maxWidth="md"
				open={open}
				onClose={() => {
					setOpen(false);
				}}
				aria-labelledby="table-dialog"
				className="backdrop"
			>
				<DialogTitle id="table-dialog">{title}</DialogTitle>
				<DialogContent>
					<CardBody className="pt-0 px-0">
						<div className="row d-flex align-items-center">
							<div className="col-md-6">
								<TextField
									id={`descripcion`}
									label="Descripción"
									value={product.descripcion}
									onChange={handleChange("descripcion")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
									required
								/>
							</div>
							<div className="col-md-6">
								<TextField
									id={`referencia`}
									label="Referencia"
									value={product.referencia}
									onChange={handleChange("referencia")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
									required
								/>
							</div>
						</div>
						<div className="row d-flex align-items-center">
							<div className="col-md-3">
								<Autocomplete
									id="autocomplete-familia"
									disableClearable
									filterSelectedOptions
									options={productFamilies}
									getOptionLabel={(option) =>
										option.descripcion
									}
									value={
										productFamilies?.find(
											(x) => x.id === product?.familia_id
										) || ""
									}
									onChange={(event, selected) => {
										setProduct({
											...product,
											familia_id: selected
												? selected.id
												: null,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Familia"
											margin="normal"
											variant="outlined"
											InputLabelProps={{
												shrink: true,
											}}
											required
										/>
									)}
								/>
							</div>
							<div className="col-md-3">
								<Autocomplete
									id="autocomplete-marca"
									filterSelectedOptions
									options={marcas}
									getOptionLabel={(option) => option.nombre}
									value={
										marcas?.find(
											(x) => x.id === product?.marca_id
										) || ""
									}
									onChange={(event, selected) => {
										setProduct({
											...product,
											marca_id: selected ? selected.id : null,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Marca"
											margin="normal"
											variant="outlined"
											InputLabelProps={{ shrink: true }}
										/>
									)}
								/>
							</div>
							<div className="col-md-6 d-flex align-items-center">
								<Button
									className="align-self-center"
									style={{ whiteSpace: "nowrap", flexShrink: 0 }}
									variant="outlined"
									color="primary"
									onClick={() => setOpenCreateMarcaDialog(true)}
								>
									Nueva marca
								</Button>
							</div>
						</div>
						<div className="row d-flex align-items-center">
							<div className="col-md-3">
								<Autocomplete
									id="autocomplete-tipo"
									disableClearable
									filterSelectedOptions
									options={productTypes}
									getOptionLabel={(option) =>
										option.descripcion
									}
									value={
										productTypes?.find(
											(x) =>
												x.id ===
												product?.tipo_producto_id
										) || ""
									}
									onChange={(event, selected) => {
										setProduct({
											...product,
											tipo_producto_id: selected
												? selected.id
												: null,
											precio: selected?.es_mano_de_obra ? 0 : product.precio,
											descuento_base: selected?.es_mano_de_obra ? 0 : product.descuento_base,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Tipo de Producto"
											margin="normal"
											variant="outlined"
											InputLabelProps={{
												shrink: true,
											}}
											required
										/>
									)}
								/>
							</div>
							<div className="col-md-3">
								<Autocomplete
									id="autocomplete-tipo-unidad"
									disableClearable
									filterSelectedOptions
									options={productUnitTypes}
									getOptionLabel={(option) =>
										option.descripcion
									}
									value={
										productUnitTypes?.find(
											(x) =>
												x.id === product?.tipo_unidad_id
										) || ""
									}
									onChange={(event, selected) => {
										setProduct({
											...product,
											tipo_unidad_id: selected
												? selected.id
												: null,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Tipo de Unidad"
											margin="normal"
											variant="outlined"
											InputLabelProps={{
												shrink: true,
											}}
											required
										/>
									)}
								/>
							</div>
						</div>
						<div className="row d-flex align-items-center">
							<div className="col-md-4">
								<TextField
									id={`precio`}
									label="Precio (€)"
									value={isLabor ? 0 : product.precio}
									onChange={handleChange("precio")}
									disabled={isLabor}
									helperText={isLabor ? "Se calcula con el coste/hora de la oferta." : undefined}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										inputProps: {
											min: 0,
										},
									}}
									type="number"
									margin="normal"
									variant="outlined"
									required
								/>
							</div>
							<div className="col-md-4">
								<DurationTextField
									id={`tiempo_instalacion`}
									label="Tiempo de instalación (hh:mm:ss)"
									value={product.tiempo_instalacion}
									onDurationChange={handleInstallationTimeChange}
									resetKey={open}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
									required
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`descuento`}
									label="Descuento base"
									value={isLabor ? 0 : product.descuento_base}
									onChange={handleChange("descuento_base")}
									disabled={isLabor}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										inputProps: {
											min: 0,
										},
									}}
									type="number"
									margin="normal"
									variant="outlined"
								/>
							</div>
						</div>
					</CardBody>
				</DialogContent>
				<DialogActions>
					<Button
						className="mr-2"
						onClick={() => {
							setOpen(false);
							setProduct(getEmptyProduct());
						}}
						variant="outlined"
					>
						Cancelar
					</Button>
					<Button
						onClick={() => {
							saveProduct();
						}}
						variant="outlined"
						color="primary"
					>
						Guardar
					</Button>
					<br />
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth
				maxWidth="xs"
				open={openCreateMarcaDialog}
				onClose={() => setOpenCreateMarcaDialog(false)}
				className="backdrop"
			>
				<DialogTitle>Nueva marca</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						required
						label="Nombre"
						value={newMarcaName}
						onChange={(event) => setNewMarcaName(event.target.value)}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenCreateMarcaDialog(false)} variant="outlined">Cancelar</Button>
					<Button onClick={saveMarca} variant="outlined" color="primary">Guardar</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
export default CreateProductDialog;
